import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
  LinearProgress,
  Skeleton,
  Collapse,
  Stack,
} from "@mui/material";
import {
  ContentCopy,
  Add,
  Refresh,
  CloudUpload,
  CheckCircle,
  Replay,
  Circle,
  OpenInNew,
  VpnKey,
  DeleteOutline,
  ExpandMore,
  ExpandLess,
  SwapHoriz,
} from "@mui/icons-material";
import {
  generateMigrationToken,
  listMigrationTokens,
  revokeMigrationToken,
  listExternalMigrations,
  retryExternalMigration,
  deleteExternalMigration,
  clearGeneratedToken,
  updateMigrationProgress,
} from "../redux/slices/externalMigrationSlice";
import {
  socket,
  onExternalMigrationProgress,
  offExternalMigrationProgress,
  onMigrationProgress,
  offMigrationProgress,
} from "../services/socket";

// ─────────────────────────────────────────────────────────────────────────────
// Status styles (matching Applications page pattern)
// ─────────────────────────────────────────────────────────────────────────────

const migrationStatusStyles = {
  CREATED: { color: "#6b7280", bg: "#f3f4f6", label: "Created" },
  RECEIVING: { color: "#3b82f6", bg: "#dbeafe", label: "Receiving" },
  ASSEMBLING: { color: "#3b82f6", bg: "#dbeafe", label: "Assembling" },
  IMPORTING: { color: "#f59e0b", bg: "#fef3c7", label: "Importing" },
  VERIFYING: { color: "#f59e0b", bg: "#fef3c7", label: "Verifying" },
  COMPLETE: { color: "#10b981", bg: "#d1fae5", label: "Complete" },
  FAILED: { color: "#ef4444", bg: "#fee2e2", label: "Failed" },
  ROLLED_BACK: { color: "#6b7280", bg: "#f3f4f6", label: "Rolled Back" },
};

const tokenStatusStyles = {
  ACTIVE: { color: "#10b981", bg: "#d1fae5", label: "Active" },
  USED: { color: "#3b82f6", bg: "#dbeafe", label: "Used" },
  EXPIRED: { color: "#6b7280", bg: "#f3f4f6", label: "Expired" },
  REVOKED: { color: "#ef4444", bg: "#fee2e2", label: "Revoked" },
};

const DELETABLE_STATUSES = [
  "FAILED",
  "COMPLETE",
  "ROLLED_BACK",
  "CREATED",
  "RECEIVING",
  "ASSEMBLING",
  "IMPORTING",
  "VERIFYING",
];

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export default function ImportSite() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    generatedToken,
    generateLoading,
    generateError,
    tokens,
    tokensLoading,
    migrations,
    migrationsLoading,
  } = useSelector((state) => state.externalMigration);

  const [copied, setCopied] = useState({ url: false, token: false });
  const [label, setLabel] = useState("");
  const [showTokens, setShowTokens] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState({});

  useEffect(() => {
    dispatch(listMigrationTokens());
    dispatch(listExternalMigrations({ limit: 20 }));
  }, [dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(listExternalMigrations({ limit: 20 }));
    }, 10000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // Socket listeners for real-time migration progress
  useEffect(() => {
    const handleProgress = (data) => {
      dispatch(updateMigrationProgress(data));
      // If migration completed or failed, refresh the full list
      if (data.status === "COMPLETE" || data.status === "FAILED") {
        dispatch(listExternalMigrations({ limit: 20 }));
      }
    };

    onExternalMigrationProgress(handleProgress);
    onMigrationProgress(handleProgress);

    return () => {
      offExternalMigrationProgress(handleProgress);
      offMigrationProgress(handleProgress);
    };
  }, [dispatch]);

  const handleGenerateToken = useCallback(() => {
    dispatch(generateMigrationToken({ label: label || undefined }));
    setLabel("");
  }, [dispatch, label]);

  const handleCopy = useCallback((text, field) => {
    navigator.clipboard.writeText(text);
    setCopied((prev) => ({ ...prev, [field]: true }));
    setTimeout(() => setCopied((prev) => ({ ...prev, [field]: false })), 2000);
  }, []);

  const handleRevokeToken = useCallback(
    (tokenId) => {
      dispatch(revokeMigrationToken(tokenId));
    },
    [dispatch],
  );

  const handleRetryMigration = useCallback(
    (migrationId) => {
      dispatch(retryExternalMigration(migrationId));
    },
    [dispatch],
  );

  const handleDeleteMigration = useCallback(
    (migrationId) => {
      if (
        window.confirm(
          "Delete this migration and clean up its resources? This cannot be undone.",
        )
      ) {
        dispatch(deleteExternalMigration(migrationId));
      }
    },
    [dispatch],
  );

  const handleRefresh = useCallback(() => {
    dispatch(listMigrationTokens());
    dispatch(listExternalMigrations({ limit: 20 }));
  }, [dispatch]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Import External Site
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Migrate a WordPress site from any host to WP Spark
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              if (generatedToken) dispatch(clearGeneratedToken());
            }}
            disableElevation
            disabled={!!generatedToken}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Generate Token
          </Button>
        </Stack>
      </Box>

      {/* ── Token Generation ───────────────────────────────────────────── */}
      {!generatedToken && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 3,
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                flexShrink: 0,
              }}
            >
              <VpnKey sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Generate Migration Token
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a one-time token to paste into the WP Spark Migrate
                plugin on your existing site
              </Typography>
            </Box>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Token label (optional)"
              sx={{
                flex: 1,
                maxWidth: 400,
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
            />
            <Button
              variant="contained"
              onClick={handleGenerateToken}
              disabled={generateLoading}
              disableElevation
              startIcon={
                generateLoading ? <CircularProgress size={16} /> : <Add />
              }
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              {generateLoading ? "Generating..." : "Generate Token"}
            </Button>
          </Stack>

          {generateError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {generateError.error ||
                generateError.message ||
                "Failed to generate token"}
            </Alert>
          )}

          {/* Instructions toggle */}
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setShowInstructions(!showInstructions)}
              endIcon={showInstructions ? <ExpandLess /> : <ExpandMore />}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              How it works
            </Button>
            <Collapse in={showInstructions}>
              <Box
                sx={{
                  mt: 1.5,
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                }}
              >
                {[
                  "Install the WP Spark Migrate plugin on your existing WordPress site",
                  "Go to WP Spark → Migrate in WordPress admin",
                  "Paste the Platform URL and Migration Token",
                  "Follow the 6-step wizard — the plugin exports and pushes your site",
                  "WP Spark creates a new site, imports files + database, and rewrites URLs",
                ].map((step, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      mb: i < 4 ? 1 : 0,
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        </Box>
      )}

      {/* ── Generated Token Display ────────────────────────────────────── */}
      {generatedToken && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "success.main",
            borderRadius: 2,
            p: 3,
            mb: 3,
            bgcolor: "success.main",
            backgroundImage:
              "linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(16,185,129,0.02) 100%)",
          }}
        >
          <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 3 }}>
            <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
              <strong>Token generated!</strong> Copy both values below into the
              WP Spark Migrate plugin. The token will not be shown again.
            </Alert>

            {[
              {
                label: "Platform URL",
                value: generatedToken.platformUrl || "",
                field: "url",
                mono: false,
              },
              {
                label: "Migration Token",
                value: generatedToken.token || "",
                field: "token",
                mono: true,
              },
            ].map((item) => (
              <Box
                key={item.field}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.5,
                  mb: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "action.hover",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontFamily: item.mono ? "monospace" : "inherit",
                      fontSize: item.mono ? "0.8rem" : undefined,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
                <Tooltip
                  title={copied[item.field] ? "Copied!" : "Copy to clipboard"}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(item.value, item.field)}
                    color={copied[item.field] ? "success" : "default"}
                  >
                    {copied[item.field] ? (
                      <CheckCircle fontSize="small" />
                    ) : (
                      <ContentCopy fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            ))}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 2,
              }}
            >
              <Typography variant="caption" color="warning.main">
                Expires: {new Date(generatedToken.expiresAt).toLocaleString()}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => dispatch(clearGeneratedToken())}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Generate Another
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Migrations List ────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Migrations
        </Typography>

        {/* Loading skeletons */}
        {migrationsLoading && migrations.length === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={68}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
        )}

        {/* Empty state */}
        {!migrationsLoading && migrations.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: "2px dashed",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <CloudUpload sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" fontWeight={600} gutterBottom>
              No migrations yet
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 400, mx: "auto" }}
            >
              Generate a token above and install the WP Spark Migrate plugin on
              your existing WordPress site to start
            </Typography>
          </Box>
        )}

        {/* Migration rows */}
        {migrations.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {migrations.map((migration) => {
              const status =
                migrationStatusStyles[migration.status] ||
                migrationStatusStyles.CREATED;
              const isActive = [
                "RECEIVING",
                "ASSEMBLING",
                "IMPORTING",
                "VERIFYING",
              ].includes(migration.status);

              let sourceLabel = migration.sourceUrl;
              try {
                sourceLabel = new URL(migration.sourceUrl).hostname;
              } catch {
                /* keep full URL */
              }

              return (
                <Box
                  key={migration.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    transition: "all 0.15s",
                    overflow: "hidden",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                    }}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: status.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <SwapHoriz sx={{ fontSize: 18, color: status.color }} />
                    </Box>

                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.25,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {sourceLabel}
                        </Typography>
                        {migration.sourceHost && (
                          <Chip
                            size="small"
                            label={migration.sourceHost}
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 500,
                              bgcolor: "action.selected",
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {new Date(migration.createdAt).toLocaleDateString()}
                        {migration.site &&
                          ` · ${migration.site.name || migration.site.domain}`}
                        {migration.status === "FAILED" &&
                          migration.errorMessage && (
                            <Tooltip title={migration.errorMessage}>
                              <span>
                                {" "}
                                · {migration.errorMessage.substring(0, 80)}…
                              </span>
                            </Tooltip>
                          )}
                      </Typography>
                      {isActive && (
                        <>
                          {migration._message && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                fontStyle: "italic",
                                display: "block",
                              }}
                            >
                              {migration._message}
                            </Typography>
                          )}
                          <LinearProgress
                            variant={
                              migration._progress > 0
                                ? "determinate"
                                : "indeterminate"
                            }
                            value={migration._progress || 0}
                            sx={{ mt: 0.5, borderRadius: 1, height: 4 }}
                          />
                        </>
                      )}
                    </Box>

                    {/* Status chip */}
                    <Chip
                      size="small"
                      icon={
                        <Circle
                          sx={{
                            fontSize: "8px !important",
                            color: `${status.color} !important`,
                          }}
                        />
                      }
                      label={status.label}
                      sx={{
                        bgcolor: status.bg,
                        color: status.color,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        height: 26,
                        minWidth: 90,
                        "& .MuiChip-icon": { ml: 0.5 },
                        display: { xs: "none", sm: "flex" },
                      }}
                    />

                    {/* Actions — View site only for COMPLETE + ACTIVE */}
                    {migration.site &&
                      migration.status === "COMPLETE" &&
                      migration.site.status === "ACTIVE" && (
                        <Tooltip title="View site">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/dashboard/sites/${migration.site.id}`)
                            }
                            sx={{ color: "text.secondary" }}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    {migration.status === "FAILED" && (
                      <Tooltip title="Retry import">
                        <IconButton
                          size="small"
                          onClick={() => handleRetryMigration(migration.id)}
                          sx={{ color: "text.secondary" }}
                        >
                          <Replay fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {DELETABLE_STATUSES.includes(migration.status) && (
                      <Tooltip title="Delete migration">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMigration(migration.id)}
                          sx={{
                            color: "text.disabled",
                            "&:hover": { color: "error.main" },
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Logs toggle */}
                    {migration.logs && migration.logs.length > 0 && (
                      <Tooltip
                        title={
                          expandedLogs[migration.id] ? "Hide logs" : "Show logs"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedLogs((prev) => ({
                              ...prev,
                              [migration.id]: !prev[migration.id],
                            }))
                          }
                          sx={{ color: "text.secondary" }}
                        >
                          {expandedLogs[migration.id] ? (
                            <ExpandLess fontSize="small" />
                          ) : (
                            <ExpandMore fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Logs panel */}
                  <Collapse in={!!expandedLogs[migration.id]}>
                    <Box
                      sx={{
                        px: 2,
                        pb: 2,
                        pt: 0,
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          display: "block",
                          py: 1,
                          color: "text.secondary",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Import Logs
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: 240,
                          overflowY: "auto",
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "grey.900"
                              : "grey.50",
                          borderRadius: 1,
                          p: 1.5,
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                        }}
                      >
                        {(migration.logs || []).map((log) => (
                          <Box
                            key={log.id}
                            sx={{
                              display: "flex",
                              gap: 1,
                              py: 0.3,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              "&:last-child": { borderBottom: "none" },
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.72rem",
                                color: "text.disabled",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}
                            >
                              {new Date(log.createdAt).toLocaleTimeString()}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.72rem",
                                color: "primary.main",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                minWidth: 80,
                              }}
                            >
                              {log.step}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.72rem",
                                color:
                                  log.level === "error"
                                    ? "error.main"
                                    : "text.primary",
                                wordBreak: "break-word",
                              }}
                            >
                              {log.message}
                            </Typography>
                          </Box>
                        ))}
                        {(!migration.logs || migration.logs.length === 0) && (
                          <Typography variant="caption" color="text.disabled">
                            No logs available yet
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ── Token History (collapsible) ────────────────────────────────── */}
      <Box>
        <Box
          onClick={() => setShowTokens(!showTokens)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            mb: showTokens ? 2 : 0,
            py: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Migration Tokens
            {tokens.length > 0 && (
              <Chip
                size="small"
                label={tokens.length}
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  bgcolor: "action.selected",
                }}
              />
            )}
          </Typography>
          <IconButton size="small">
            {showTokens ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={showTokens}>
          {tokensLoading && tokens.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[1, 2].map((i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={52}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Box>
          )}

          {!tokensLoading && tokens.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              No tokens generated yet
            </Typography>
          )}

          {tokens.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {tokens.map((token) => {
                const tStatus =
                  tokenStatusStyles[token.status] || tokenStatusStyles.ACTIVE;

                return (
                  <Box
                    key={token.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.5,
                      px: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      transition: "all 0.15s",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: "action.selected",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <VpnKey sx={{ fontSize: 16, color: "text.secondary" }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {token.label || "Untitled token"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        Created {new Date(token.createdAt).toLocaleDateString()}{" "}
                        · Expires {new Date(token.expiresAt).toLocaleString()}
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      icon={
                        <Circle
                          sx={{
                            fontSize: "8px !important",
                            color: `${tStatus.color} !important`,
                          }}
                        />
                      }
                      label={tStatus.label}
                      sx={{
                        bgcolor: tStatus.bg,
                        color: tStatus.color,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        height: 24,
                        minWidth: 72,
                        "& .MuiChip-icon": { ml: 0.5 },
                      }}
                    />

                    {token.status === "ACTIVE" ? (
                      <Tooltip title="Revoke token">
                        <IconButton
                          size="small"
                          onClick={() => handleRevokeToken(token.id)}
                          sx={{
                            color: "text.disabled",
                            "&:hover": { color: "error.main" },
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box sx={{ width: 34 }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Collapse>
      </Box>
    </Box>
  );
}
